/* eslint-disable react-hooks/set-state-in-render --
 * The page-preset memo sets state while resolving a preset; it needs a behavior-preserving
 * refactor of its own. */
import './CrossChannel.scss';
import * as R from 'ramda';
import {
  Dispatch,
  JSX,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KpiInfo, Metric } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import * as CC from '@blisspointmedia/bpm-types/dist/CrossChannelPerformance';
import { PlatformOptions } from '@blisspointmedia/bpm-types/dist/App2Performance';
import {
  GetCustomMetricsParams,
  GetCustomMetricsResponse
} from '@blisspointmedia/bpm-types/dist/CustomMetrics';
import {
  DateInfo,
  DeletePresetParams as DeletePagePresetParams,
  GetPresetsParams as GetPagePresetsParams,
  MetricsPagePreset,
  SavePresetParams as SavePagePresetParams,
  SavePresetResponse as SavePagePresetResponse
} from '@blisspointmedia/bpm-types/dist/MetricsPage';
import {
  CROSS_CHANNEL,
  DEFAULT_DATE_RANGE_STATE,
  DEFAULT_OTHER_DATE_RANGE_STATE,
  DimensionColumn,
  GetPresetsParams as GetTablePresetsParams,
  PresetIDGroups as SimpleMetricsTablePreset
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { FilterPreset } from '@blisspointmedia/bpm-types/dist/pgTables/FilterPreset';
import { computeResolvedDate } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { DatePicker } from '@Components/DatePicker';
import { Intent, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Toolbar } from '@Components/Toolbar';
import { FetchFilterBar, useFilterPaneState } from '@Components/FetchFilterBar/FetchFilterBar';
import {
  ColumnMetaData,
  DimensionMap,
  convertCustomMetric,
  useGroupedSegmentFiltering
} from '@Components/MetricsTable/metrics-table-utils';
import { Page } from '@Components/Page';
import { PresetDropdown } from '@Components/PresetDropdown/PresetDropdown';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { FullPageSpinner } from '@Components/Spinner';
import { useGroupedSegments } from '@Pages/SegmentationMapping/segmentationMappingUtils';
import { useAppSelector } from '@src/redux';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import * as UserRedux from '@src/redux/user';
import { getChannelSeriesColor, shouldUsePrimaryPalette } from '@src/utils/colors';
import {
  comparisonRangeBefore,
  DateRange,
  resolvePresetDateInfo,
  parseBase64GzippedResponse
} from '@src/utils/date-utils';
import { dataInfoExcelExport, getDataInfo } from '@src/utils/download-utils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import {
  awaitJSON,
  CrossChannelLambdaFetch,
  SingleChannelLambdaFetch,
  ToolsLambdaFetch
} from '@src/utils/fetch-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { setPresetSearchParams } from '@src/utils/routing/preset-url-utils';
import { usePresetUrl } from '@src/utils/routing/usePresetUrl';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { STANDARD_GROUP_NAME } from '@src/utils/performance-utils';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { SourcePicker, SourceType, deriveSourceType } from './Components/SourcePicker';
import { CrossChannelContext } from './cross-channel-context';
import { CrossChannelWidget } from './cross-channel-constants';
import {
  buildCustomSegmentDimensionMap,
  calculateOverviewMetricsByDay,
  dimensionColumnMetaDataMap,
  getCrossChannelColumnMetaData,
  getCrossChannelDimensionCell
} from './cross-channel-utils';
import { EditPagePresetModal } from './EditPagePresetModal';
import { Overview } from './Tabs/Overview';

const DeliveryAndPerformance = lazy(() =>
  import('./Tabs/DeliveryAndPerformance').then(m => ({ default: m.DeliveryAndPerformance }))
);
const Metrics = lazy(() =>
  import('./Tabs/MetricsTableView').then(m => ({ default: m.MetricsTableView }))
);
const TotalBusiness = lazy(() =>
  import('./Tabs/TotalBusiness').then(m => ({ default: m.TotalBusiness }))
);

export const TabKey = {
  OVERVIEW: 'overview',
  DELIVERY_AND_PERFORMANCE: 'delivery',
  METRICS_TABLE: 'metrics-table',
  TOTAL_BUSINESS: 'total-business'
} as const;
export type TabKeyType = (typeof TabKey)[keyof typeof TabKey];

const NAVS = [
  { label: 'Overview', key: TabKey.OVERVIEW },
  { label: 'Delivery & Performance', key: TabKey.DELIVERY_AND_PERFORMANCE },
  { label: 'Metrics Table', key: TabKey.METRICS_TABLE },
  { label: 'Total Business', key: TabKey.TOTAL_BUSINESS }
];

// `LOCATIONS['Cross-Channel'].subPages.Overview.testCompanyParams` doesn't exist in this repo's
// routing config — it was always just this fixed shape, so it's hardcoded locally.
const CROSS_CHANNEL_TEST_COMPANY_PARAMS = { channel: CROSS_CHANNEL, pageID: CROSS_CHANNEL };

export const CrossChannel = () => {
  // Check if we're on the experimental branch
  const isExperimental =
    process.env.REACT_APP_DOCKER_LAMBDA_HOST === 'https://api-staging-2.blisspointmedia.com' ||
    window.location.hostname === 'localhost';
  const location = useLocation();
  const navigate = useNavigate();
  const { pagePresetName: urlPagePresetName, tablePresetName: urlTablePresetName } = usePresetUrl();
  const company = useLocationCompany() ?? '';
  const companyInfo = useCompanyInfo();
  const { defaultCrossChannelSource } = companyInfo;
  const { fiscal_cal: fiscalCal } = companyInfo;
  const prefix = CROSS_CHANNEL;
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const confirmSave = useCallback(
    (onOkay: () => void, options?: { temporary?: boolean }) => {
      const temporary = options?.temporary;
      setAreYouSure({
        title: temporary ? 'Save Temporary View' : 'Save Preset',
        message: temporary
          ? "You're saving a temporary view that will only be available during this session unless you later edit and save it as a Table Preset during this session."
          : 'Saving this preset cannot be undone. Are you sure?',
        okayText: 'Save',
        cancelText: 'Cancel',
        intent: Intent.WARNING,
        onOkay
      });
    },
    [setAreYouSure]
  );
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);

  // State Variables
  const [budgetsDataMap, setBudgetsDataMap] = useState<Record<string, any>>({});
  const [failedBudgetsDataKey, setFailedBudgetsDataKey] = useState<string>();
  const [marketingInputsMap, setMarketingInputsMap] = useState<
    Record<string, { channelSpend: any[]; channelClassification: Record<string, string> }>
  >({});
  const [customColumnMetaDataMap, setCustomColumnMetaDataMap] =
    useState<Record<string, ColumnMetaData>>();
  const [customSegments, setCustomSegments] = useState<Record<string, any>>({});
  const [dates, setDates] = useState<DateRange | undefined>();
  const [fetchingPagePresets, setFetchingPagePresets] = useState(false);
  const [fetchingTablePresets, setFetchingTablePresets] = useState(false);
  const [filterData, setFilterData] = useState<CC.GetFilterOptionsResponse>();
  const [filterID, setFilterID] = useState<number | undefined>();
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>();
  const [filterSource, setFilterSource] = useState<
    'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState'
  >();
  const [filterState, setFilterState, resetFilterState] = useFilterPaneState();
  const [kpi, setKpi] = useState<string | undefined>();
  const [platformOptions, setPlatformOptions] = useState<PlatformOptions>([]);
  const [kpiDataMap, setKpiDataMap] = useState<Record<string, KpiInfo>>({});
  // The last key whose fetch failed; cleared when the key changes so a revisit retries.
  const [failedKpiDataKey, setFailedKpiDataKey] = useState<string>();
  const [sourceOptions, setSourceOptions] = useState<
    { label: string; value: string; iconUrl?: string }[]
  >([]);
  const [selectedSources, setSelectedSources] = useState<string[]>(
    defaultCrossChannelSource ? [defaultCrossChannelSource] : []
  );
  const [totalBusinessSource, setTotalBusinessSource] = useState<string | undefined>(
    defaultCrossChannelSource || undefined
  );
  const [metricsDataMap, setMetricsDataMap] = useState<Record<string, any>>({});
  const [metricsTableDataMap, setMetricsTableDataMap] = useState<Record<string, any>>({});
  const [metricsTableFetchFilterBar, setMetricsTableFetchFilterBar] = useState<JSX.Element>();
  const [otherDates, setOtherDates] = useState<DateRange | undefined>();
  const [pagePresetChanges, setPagePresetChanges] = useState<MetricsPagePreset>({
    globalOptions: {}
  } as MetricsPagePreset);
  const [pagePresetName, setPagePresetName] = useState<string>();
  const [pagePresets, setPagePresets] = useState<MetricsPagePreset[]>();
  const [selectedChannels, setSelectedChannels] = useState<Record<string, boolean>>({});
  const [selectedTab, setSelectedTab] = useState<string>();
  const [showEditPagePresetModal, setShowEditPagePresetModal] = useState(false);
  const [showNewPagePresetModal, setShowNewPagePresetModal] = useState(false);
  const [showRenamePagePresetModal, setShowRenamePagePresetModal] = useState(false);
  const [tablePresetName, setTablePresetName] = useState<string>();
  const [tablePresets, setTablePresets] = useState<SimpleMetricsTablePreset[]>();
  const [metricsDataLoading, setMetricsDataLoading] = useState<boolean>(false);
  const [deliveryAndPerformanceChannels, setDeliveryAndPerformanceChannels] = useState<
    Record<string, boolean>
  >({});

  const { currency_code: currencyCode } = companyInfo;
  const columnMetaDataMap = useMemo(
    () => getCrossChannelColumnMetaData(currencyCode),
    [currencyCode]
  );
  const showCCDataMarketingInputs = useExperimentFlag('showCCDataMarketingInputs');
  const shouldEnableMetricsTableSegmentGroups = useExperimentFlag(
    'enableMetricsTableSegmentGroups'
  ) as boolean;

  const { groupedSegments } = useGroupedSegments({
    company,
    shouldEnableMetricsTableSegmentGroups
  });
  const usedGroupedSegments = useGroupedSegmentFiltering(filterState, groupedSegments);

  const platformAsSourceOfData = useMemo(
    () => deriveSourceType(selectedSources) !== SourceType.ANALYTICS,
    [selectedSources]
  );

  // Mounted at one route per tab (`.../cross-channel/<tab>`); the base path is built from
  // `company`, since the pathname already contains the mounted tab.
  const channelBasePath = useMemo(() => `/${company}/cross-channel`, [company]);

  const budgetsDataKey = useMemo(() => {
    if (!dates || !otherDates || !filterState) {
      return '';
    }
    return `${company}_${dates.start}_${dates.end}_${otherDates.start}_${
      otherDates.end
    }_${JSON.stringify(filterState)}`;
  }, [company, dates, otherDates, filterState]);

  const marketingInputsKey = useMemo(
    () => (dates ? `${company}_${dates.start}_${dates.end}_${companyInfo.initial_kpi}` : ''),
    [company, dates, companyInfo.initial_kpi]
  );

  const kpiDataKey = useMemo(
    () => (dates ? `${company}_${dates.start}_${dates.end}_${totalBusinessSource}` : ''),
    [company, dates, totalBusinessSource]
  );

  const metricsTableDataKey = useMemo(
    () =>
      dates
        ? `${tablePresetName}_${filterID}_${JSON.stringify(filterState)}_${kpi}_${dates.start}_${
            dates.end
          }_${selectedSources.join('')}`
        : '',
    [dates, filterID, filterState, kpi, tablePresetName, selectedSources]
  );

  const metricsTableDataOtherKey = useMemo(
    () =>
      otherDates
        ? `${tablePresetName}_${filterID}_${JSON.stringify(filterState)}_${kpi}_${
            otherDates.start
          }_${otherDates.end}_${platformAsSourceOfData ? 'platform' : 'analytics'}`
        : '',
    [otherDates, filterID, filterState, kpi, tablePresetName, platformAsSourceOfData]
  );

  const [startingDateRange, rememberedOtherDateRange] = useMemo(() => {
    const localDateRangeString = localStorage.getItem(`${company}_crossChannelDateRange`);
    const localDateRange = localDateRangeString ? JSON.parse(localDateRangeString) : null;
    const localOtherDateRangeString = localStorage.getItem(`${company}_crossChannelOtherDateRange`);
    const localOtherDateRange = localOtherDateRangeString
      ? JSON.parse(localOtherDateRangeString)
      : null;
    return [
      {
        start:
          localDateRange && localDateRange.start
            ? localDateRange.start
            : computeResolvedDate(DEFAULT_DATE_RANGE_STATE.start),
        end:
          localDateRange && localDateRange.end
            ? localDateRange.end
            : computeResolvedDate(DEFAULT_DATE_RANGE_STATE.end)
      },
      {
        start:
          localOtherDateRange && localOtherDateRange.start
            ? localOtherDateRange.start
            : computeResolvedDate(DEFAULT_OTHER_DATE_RANGE_STATE.start),
        end:
          localOtherDateRange && localOtherDateRange.end
            ? localOtherDateRange.end
            : computeResolvedDate(DEFAULT_OTHER_DATE_RANGE_STATE.end)
      }
    ];
  }, [company]);
  // A remembered/default comparison range that doesn't end before the primary starts is ignored in
  // favor of the default comparison derived from the primary dates.
  const startingOtherDateRange = useMemo(
    () => comparisonRangeBefore(startingDateRange, rememberedOtherDateRange, fiscalCal),
    [startingDateRange, rememberedOtherDateRange, fiscalCal]
  );

  const kpiData = useMemo(() => kpiDataMap[kpiDataKey], [kpiDataMap, kpiDataKey]);
  const crossChannelKPIs = useMemo(() => {
    const kpiMap: Record<string, boolean> = {};
    if (
      isExperimental &&
      companyInfo &&
      companyInfo.cid === 'tinuititest' &&
      kpiData &&
      kpiData.kpiData
    ) {
      for (const row of kpiData.kpiData) {
        kpiMap[row.kpi] = true;
      }
      return R.keys(kpiMap);
    }
    return R.defaultTo([], companyInfo.cross_channel_kpis);
  }, [companyInfo, isExperimental, kpiData]);

  const budgetsData = useMemo(
    () => R.pathOr([], [budgetsDataKey], budgetsDataMap),
    [budgetsDataMap, budgetsDataKey]
  );

  const channelColors = useMemo(() => {
    const channels = R.keys(selectedChannels).sort();
    const usePrimaryPalette = shouldUsePrimaryPalette(channels.length);
    return channels.reduce(
      (prev, curr, i) => ({
        ...prev,
        [curr]: getChannelSeriesColor(
          i,
          usePrimaryPalette,
          TEST_COMPANIES.includes(company) || company.includes('tinuitidemo')
        )
      }),
      {}
    );
  }, [company, selectedChannels]);

  const resetPageSettings = useCallback(() => {
    setSelectedSources([]);
    setKpi(undefined);
    setDates(undefined);
    setOtherDates(undefined);
    setFilterID(undefined);
    setFilterSource(undefined);
  }, []);

  // URL Callback
  const setURL = useCallback(
    (pagePresetNameInput: string, tablePresetNameInput?: string, selectedTabInput?: string) => {
      // To avoid infinite renders, only update the URL and page preset if it's different from
      // the current state. A `selectedTabInput` that differs from the currently-mounted tab
      // navigates to one of the OTHER top-level `/cross-channel/<tab>` routes, which is a full
      // remount of this component (see the routing note on `channelBasePath` above) — that's
      // expected, not a bug.
      const newPath = `${channelBasePath}/${R.defaultTo(selectedTab, selectedTabInput)}`;
      const newSearch = setPresetSearchParams(location.search, {
        pagePresetName: pagePresetNameInput,
        tablePresetName: tablePresetNameInput
      });
      const isLocationDifferent = location.pathname !== newPath || location.search !== newSearch;
      if (
        (selectedTab && isLocationDifferent) ||
        R.isNil(pagePresetName) ||
        R.isNil(tablePresetName)
      ) {
        setPagePresetName(pagePresetNameInput);
        setTablePresetName(tablePresetNameInput);
        navigate(`${newPath}${newSearch}`);
      }
    },
    [location, navigate, channelBasePath, selectedTab, pagePresetName, tablePresetName]
  );

  // Dates Callbacks
  const onDatePickerChange = useCallback(
    (newDates: DateRange) => {
      localStorage.setItem(`${company}_crossChannelDateRange`, JSON.stringify(newDates));
      setDates(newDates);
    },
    [company, setDates]
  );

  const onOtherDatePickerChange = useCallback(
    (newOtherDates: DateRange) => {
      localStorage.setItem(`${company}_crossChannelOtherDateRange`, JSON.stringify(newOtherDates));
      setOtherDates(newOtherDates);
    },
    [company, setOtherDates]
  );

  // Page Preset
  const pagePreset = useMemo(() => {
    if (
      pagePresets &&
      tablePresets &&
      !fetchingPagePresets &&
      !fetchingTablePresets &&
      sourceOptions
    ) {
      const foundPagePreset = R.find(
        (preset: MetricsPagePreset) => preset.name === pagePresetName,
        pagePresets
      );
      if (foundPagePreset) {
        const newPagePreset = R.clone(foundPagePreset);
        const newPagePresetChanges = R.clone(foundPagePreset);
        if (newPagePreset.dateInfo) {
          const { primary: resolvedPrimaryDates, comparison: resolvedComparisonDates } =
            resolvePresetDateInfo(newPagePreset.dateInfo, fiscalCal, startingOtherDateRange);
          if (resolvedPrimaryDates) {
            newPagePreset.dateInfo = {
              ...R.defaultTo({}, newPagePreset.dateInfo),
              ...resolvedPrimaryDates
            } as DateInfo;
          } else {
            setDates(startingDateRange);
            newPagePreset.dateInfo = {
              ...R.defaultTo({}, newPagePreset.dateInfo),
              ...startingDateRange
            } as DateInfo;
          }
          if (R.isNil(dates)) {
            setDates(newPagePreset.dateInfo);
            newPagePresetChanges.dateInfo = newPagePreset.dateInfo;
          } else {
            newPagePresetChanges.dateInfo = {
              ...R.defaultTo({}, newPagePresetChanges.dateInfo),
              ...dates
            } as DateInfo;
          }
          newPagePreset.dateInfo = {
            ...newPagePreset.dateInfo,
            comparison_start: resolvedComparisonDates.start,
            comparison_end: resolvedComparisonDates.end
          };
          if (R.isNil(otherDates)) {
            setOtherDates({
              start: newPagePreset.dateInfo.comparison_start as string,
              end: newPagePreset.dateInfo.comparison_end as string
            });
            newPagePresetChanges.dateInfo = newPagePreset.dateInfo;
          } else {
            newPagePresetChanges.dateInfo = {
              ...R.defaultTo({}, newPagePresetChanges.dateInfo),
              comparison_start: otherDates.start,
              comparison_end: otherDates.end
            } as DateInfo;
          }
        } else {
          if (R.isNil(dates)) {
            setDates(startingDateRange);
            newPagePresetChanges.dateInfo = {
              ...R.defaultTo({}, newPagePresetChanges.dateInfo),
              ...startingDateRange
            } as DateInfo;
          }
          if (R.isNil(otherDates)) {
            setOtherDates(startingOtherDateRange);
            newPagePresetChanges.dateInfo = {
              ...R.defaultTo({}, newPagePresetChanges.dateInfo),
              comparison_start: startingOtherDateRange.start,
              comparison_end: startingOtherDateRange.end
            } as DateInfo;
          }
        }
        // Set Filter Info
        if (newPagePreset.filterInfo && R.isNil(filterSource)) {
          if (!R.isNil(newPagePreset.filterInfo.filterID)) {
            setFilterSource('presetFilterID');
            setFilterID(newPagePreset.filterInfo.filterID);
          } else if (newPagePreset.filterInfo.filterState) {
            setFilterSource('presetState');
            setFilterState(newPagePreset.filterInfo.filterState);
          } else {
            setFilterSource('currentState');
            resetFilterState();
          }
        } else {
          newPagePresetChanges.filterInfo = {};
          if (filterID) {
            newPagePresetChanges.filterInfo.filterID = filterID;
          } else if (filterState) {
            newPagePresetChanges.filterInfo.filterState = filterState;
          }
        }
        // Set Table Preset
        if (
          R.isNil(tablePresetName) &&
          tablePresets &&
          R.isNil(R.find(tablePreset => tablePreset.name === tablePresetName, tablePresets)) &&
          newPagePreset.globalOptions
        ) {
          const pageSourcedTableID = newPagePreset.globalOptions.tableID;
          const pageSourcedTablePreset = R.find(
            preset => preset.id === pageSourcedTableID,
            tablePresets
          );
          if (pageSourcedTablePreset) {
            setURL(newPagePreset.name, pageSourcedTablePreset.name);
          } else if (tablePresets && tablePresets.length) {
            for (const tablePreset of tablePresets) {
              if (!tablePreset.temporary) {
                newPagePreset.globalOptions.tableID = tablePreset.id;
                newPagePresetChanges.globalOptions.tableID = tablePreset.id;
                setURL(newPagePreset.name, tablePreset.name);
                break;
              }
            }
          }
        } else if (!R.isNil(tablePresetName)) {
          const pageSourcedTablePreset = R.find(
            preset => preset.name === tablePresetName,
            tablePresets
          );
          if (pageSourcedTablePreset) {
            newPagePresetChanges.globalOptions.tableID = pageSourcedTablePreset.id;
          }
        }
        if (!kpi && newPagePreset.globalOptions && newPagePreset.globalOptions.kpi) {
          const newKPI = newPagePreset.globalOptions.kpi || '';
          setKpi(newKPI);
          newPagePresetChanges.globalOptions.kpi = newKPI;
        } else if (!kpi && crossChannelKPIs) {
          const newKPI = crossChannelKPIs[0] || '';
          setKpi(newKPI);
          newPagePresetChanges.globalOptions.kpi = newKPI;
        } else if (kpi) {
          newPagePresetChanges.globalOptions.kpi = kpi;
        }
        if (R.isEmpty(selectedSources)) {
          if (newPagePreset.globalOptions && newPagePreset.globalOptions.source) {
            const source = newPagePreset.globalOptions.source || '';
            setSelectedSources([source]);
            setTotalBusinessSource(source);
            newPagePresetChanges.globalOptions.source = source;
          } else if (defaultCrossChannelSource) {
            const source = defaultCrossChannelSource || '';
            setSelectedSources([source]);
            setTotalBusinessSource(source);
            newPagePresetChanges.globalOptions.source = source;
          } else if (sourceOptions && sourceOptions[0] && sourceOptions[0].value) {
            const source = sourceOptions[0].value;
            setSelectedSources([source]);
            setTotalBusinessSource(source);
            newPagePresetChanges.globalOptions.source = source;
          }
        } else {
          newPagePresetChanges.globalOptions.source = selectedSources;
        }
        setPagePresetChanges(newPagePresetChanges);
        setPagePresetName(p => {
          if (p !== null && p !== newPagePreset.name) {
            Mixpanel.track(MxE.PagePresetChange, {
              widget: CrossChannelWidget.PAGE_PRESET,
              option_selected: newPagePreset.name
            });
          }
          return newPagePreset.name;
        });
        return newPagePreset;
      }
    }
    return undefined;
  }, [
    crossChannelKPIs,
    dates,
    defaultCrossChannelSource,
    fetchingPagePresets,
    fetchingTablePresets,
    filterID,
    filterSource,
    filterState,
    kpi,
    otherDates,
    pagePresetName,
    pagePresets,
    resetFilterState,
    selectedSources,
    setFilterState,
    setURL,
    sourceOptions,
    startingDateRange,
    startingOtherDateRange,
    tablePresetName,
    tablePresets,
    fiscalCal
  ]);

  const metricsDataKey = useMemo(
    () =>
      dates && otherDates
        ? `filtered_${filterID}_${JSON.stringify(filterState)}_${kpi}_${dates.start}_${dates.end}_${
            otherDates.start
          }_${otherDates.end}_${selectedSources.join('')}`
        : '',
    [kpi, dates, otherDates, selectedSources, filterID, filterState]
  );

  const metricsData = useMemo(
    () => metricsDataMap[metricsDataKey],
    [metricsDataMap, metricsDataKey]
  );

  const [metricTotalsByDate, otherMetricTotalsByDate] = useMemo(
    () => [
      calculateOverviewMetricsByDay(R.pathOr([], ['primaryMetrics'], metricsData)),
      calculateOverviewMetricsByDay(R.pathOr([], ['otherMetrics'], metricsData))
    ],
    [metricsData]
  );

  // In-flight request de-dup. The per-fetch `!data` / `!map[key]` guards only close AFTER the
  // (sometimes slow) fetch resolves, so a faster sibling state update re-runs the effect and
  // re-fires the still-pending request. This blocks any fetch whose key is already in flight.
  const inFlightFetches = useRef<Set<string>>(new Set());
  const dedupeFetch = useCallback(async (key: string, run: () => Promise<void>): Promise<void> => {
    if (inFlightFetches.current.has(key)) {
      return;
    }
    inFlightFetches.current.add(key);
    try {
      await run();
    } finally {
      inFlightFetches.current.delete(key);
    }
  }, []);

  // Page Preset Callbacks
  const getPagePresets = useCallback(() => {
    dedupeFetch(`metrics_page_presets_${company}`, async () => {
      try {
        setFetchingPagePresets(true);
        const res = await SingleChannelLambdaFetch<GetPagePresetsParams>('/metrics_page_presets', {
          params: {
            company,
            mediatype: prefix
          }
        });
        const parsedPresets = await awaitJSON(res);
        if (!parsedPresets.errorMessage) {
          setPagePresets(parsedPresets as MetricsPagePreset[]);
        }
      } catch (e) {
        setError({
          message: `Failed to fetch page presets. Error: ${e.message}`,
          reportError: e
        });
      } finally {
        setFetchingPagePresets(false);
      }
    });
  }, [company, prefix, setError, dedupeFetch]);

  const savePagePreset = useCallback(
    body =>
      (async () => {
        let presetResponse;
        try {
          setFetchingPagePresets(true);
          const res = await SingleChannelLambdaFetch<SavePagePresetParams>('/metrics_page_preset', {
            method: 'POST',
            body
          });
          presetResponse = await awaitJSON<SavePagePresetResponse>(res);
          return presetResponse;
        } catch (e) {
          setError({
            message: `Failed to save page preset. Error: ${e.message}`,
            reportError: e
          });
          presetResponse = { message: `Failed to save page preset. Error: ${e.message}` };
          return presetResponse;
        } finally {
          getPagePresets();
        }
      })(),
    [getPagePresets, setError]
  );

  const deletePagePreset = useCallback(
    params =>
      (async () => {
        try {
          setFetchingPagePresets(true);
          const res = await SingleChannelLambdaFetch<DeletePagePresetParams>(
            '/metrics_page_preset',
            {
              method: 'DELETE',
              params
            }
          );
          await awaitJSON(res);
        } catch (e) {
          setError({
            message: `Failed to delete page preset. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          getPagePresets();
        }
      })(),
    [getPagePresets, setError]
  );

  // Table Preset Callbacks
  const getTablePresets = useCallback(() => {
    dedupeFetch(`metrics_table_presets_${company}`, async () => {
      try {
        setFetchingTablePresets(true);
        setTablePresets(undefined);
        const res = await SingleChannelLambdaFetch<GetTablePresetsParams>(
          '/metrics_table_presets',
          {
            params: {
              company,
              mediatype: prefix
            }
          }
        );
        const parsedPresets = await awaitJSON(res);
        if (!parsedPresets.errorMessage) {
          setTablePresets(parsedPresets as SimpleMetricsTablePreset[]);
        }
      } catch (e) {
        setError({
          message: `Failed to fetch table presets. Error: ${e.message}`,
          reportError: e
        });
      } finally {
        setFetchingTablePresets(false);
      }
    });
  }, [company, prefix, setError, setTablePresets, dedupeFetch]);

  // useEffects
  // NOTE: bpm's `/crosschannel` and `/cross_channel` legacy-alias normalization effect was
  // removed here — this repo's router already redirects those dash-variants to the canonical
  // `/cross-channel` path at the route level (see `buildRoutes` in
  // `src/utils/routing/location-utils.tsx`), so `location.pathname` can never contain them by
  // the time this component renders.

  useEffect(() => {
    if (metricsDataLoading) {
      return;
    }
    (async () => {
      try {
        if (
          !metricsDataMap[metricsDataKey] &&
          dates &&
          otherDates &&
          (kpi || kpi === '') &&
          otherDates
        ) {
          const overviewParams: any = {
            company,
            start: dates.start,
            end: dates.end,
            otherStart: otherDates.start,
            otherEnd: otherDates.end,
            pageID: 0,
            kpi,
            lag: companyInfo.streaming_performance_default_lag,
            sources: selectedSources.join(','),
            channel: prefix
          };
          const paramsWithFilters: any = { ...overviewParams };

          if (filterSource === 'currentState' && !R.isNil(filterState)) {
            paramsWithFilters.filterState = JSON.stringify(filterState);
          } else if (filterSource === 'currentFilterID' && !R.isNil(filterID)) {
            paramsWithFilters.filterID = filterID;
          } else if (
            pagePreset &&
            filterSource === 'presetFilterID' &&
            !R.isNil(pagePreset.filterInfo.filterID)
          ) {
            paramsWithFilters.filterID = pagePreset.filterInfo.filterID;
          } else if (
            pagePreset &&
            filterSource === 'presetState' &&
            pagePreset.filterInfo.filterState
          ) {
            paramsWithFilters.filterState = JSON.stringify(pagePreset.filterInfo.filterState);
          }
          if (usedGroupedSegments) {
            paramsWithFilters.groupedCategories = JSON.stringify(usedGroupedSegments);
          }

          if (!metricsDataMap[metricsDataKey]) {
            setMetricsDataLoading(true);
            const metricsRes = await CrossChannelLambdaFetch('/getCrossChannelMetrics', {
              params: paramsWithFilters
            });
            const data: any = await awaitJSON(metricsRes);
            setMetricsDataMap(current => ({
              ...current,
              [metricsDataKey]: data
            }));
            const availableChannels = data.channels.reduce(
              (prev, curr) => ({ ...prev, [curr]: true }),
              {}
            );
            setDeliveryAndPerformanceChannels(availableChannels);
            setSelectedChannels(c => ({ ...c, ...availableChannels }));
            setMetricsDataLoading(false);
          }
        }
      } catch (e) {
        setMetricsDataMap(current => ({
          ...current,
          [metricsDataKey]: []
        }));
        setMetricsDataLoading(false);
        setError({
          message: `Failed to get cross channel metrics. ERROR: ${e.message}`,
          reportError: e
        });
        resetPageSettings();
      }
    })();
  }, [
    kpi,
    company,
    dates,
    otherDates,
    companyInfo.streaming_performance_default_lag,
    metricsDataMap,
    setError,
    selectedSources,
    prefix,
    customSegments,
    filterID,
    filterState,
    filterSource,
    pagePreset,
    metricsDataKey,
    metricsDataLoading,
    resetPageSettings,
    groupedSegments,
    usedGroupedSegments
  ]);

  const getCustomMetrics = useCallback(() => {
    dedupeFetch(`getCustomMetrics_${company}_${prefix}`, async () => {
      const customMetricsResp = await SingleChannelLambdaFetch<GetCustomMetricsParams>(
        '/getCustomMetrics',
        {
          params: {
            company,
            channel: prefix
          }
        }
      );
      const customMetrics = await awaitJSON<GetCustomMetricsResponse>(customMetricsResp);
      const convertedMetaDataMap: Record<string, ColumnMetaData> = {};
      for (const metric of customMetrics) {
        const convertedMetric = convertCustomMetric(metric);
        if (convertedMetric) {
          convertedMetaDataMap[metric.name] = convertedMetric;
        }
      }
      setCustomColumnMetaDataMap(convertedMetaDataMap);
    });
  }, [company, prefix, dedupeFetch]);

  // Custom metrics are independent of dates/kpi — fetch once on mount / company change.
  // (filterData, platformOptions and sourceOptions all come from getChannelFilterSourceOptions below,
  // which supersedes the former getCrossChannelFilterOptions + getSourceOfTruthOptions calls.)
  useEffect(() => {
    if (R.isNil(customColumnMetaDataMap)) {
      getCustomMetrics();
    }
  }, [customColumnMetaDataMap, getCustomMetrics]);

  useEffect(() => setFailedKpiDataKey(undefined), [kpiDataKey]);
  useEffect(() => setFailedBudgetsDataKey(undefined), [budgetsDataKey]);

  // Fire KPI and budget fetches in parallel — both gate on dates being set.
  useEffect(() => {
    const fetchKpis = async () => {
      if (
        !kpiDataMap[kpiDataKey] &&
        failedKpiDataKey !== kpiDataKey &&
        dates &&
        totalBusinessSource
      ) {
        await dedupeFetch(`getCrossChannelKpis_${kpiDataKey}`, async () => {
          try {
            const res = await CrossChannelLambdaFetch('/getCrossChannelKpis', {
              params: {
                company,
                start: dates.start,
                end: dates.end,
                source: totalBusinessSource,
                ...CROSS_CHANNEL_TEST_COMPANY_PARAMS
              }
            });
            const data = await awaitJSON(res);
            // TODO: delete this?
            const availableChannels = data.kpiData.reduce((acc, { channel }) => {
              if (channel === '') {
                return acc;
              }
              return { ...acc, [channel]: true };
            }, {});
            setKpiDataMap(current => ({ ...current, [kpiDataKey]: data }));
            setSelectedChannels(c => ({ ...c, ...availableChannels }));
          } catch (e) {
            setFailedKpiDataKey(kpiDataKey);
            setError({
              message: `Failed to get cross channel KPIs. ERROR: ${e.message}`,
              reportError: e
            });
          }
        });
      }
    };

    const fetchBudgets = async () => {
      if (
        !budgetsDataMap[budgetsDataKey] &&
        failedBudgetsDataKey !== budgetsDataKey &&
        dates &&
        otherDates
      ) {
        await dedupeFetch(`getCrossChannelBudgets_${budgetsDataKey}`, async () => {
          try {
            const body: any = {
              company,
              start: dates.start,
              end: dates.end,
              otherStart: otherDates.start,
              otherEnd: otherDates.end,
              ...CROSS_CHANNEL_TEST_COMPANY_PARAMS
            };
            if (!R.isNil(filterState)) {
              body.filterState = JSON.stringify(filterState);
            }
            const res = await CrossChannelLambdaFetch('/getCrossChannelBudgets', {
              method: 'POST',
              body
            });
            const data = await awaitJSON(res);
            setBudgetsDataMap(current => ({ ...current, [budgetsDataKey]: data }));
          } catch (e) {
            setFailedBudgetsDataKey(budgetsDataKey);
            setError({
              message: `Failed to get cross channel budgets. ERROR: ${e.message}`,
              reportError: e
            });
          }
        });
      }
    };

    Promise.all([fetchKpis(), fetchBudgets()]);
  }, [
    kpi,
    company,
    dates,
    otherDates,
    kpiDataMap,
    kpiDataKey,
    failedKpiDataKey,
    totalBusinessSource,
    budgetsDataKey,
    budgetsDataMap,
    failedBudgetsDataKey,
    filterState,
    setError,
    dedupeFetch
  ]);

  useEffect(() => {
    if (dates && !marketingInputsMap[marketingInputsKey]) {
      dedupeFetch(`getMarketingPortfolio_${marketingInputsKey}`, async () => {
        try {
          const res = await ToolsLambdaFetch('/getMarketingPortfolio', {
            params: {
              company,
              start: dates.start,
              end: dates.end,
              kpi: companyInfo.initial_kpi,
              ...CROSS_CHANNEL_TEST_COMPANY_PARAMS
            }
          });
          const { channelSpend, channelClassification } = await awaitJSON(res);
          setMarketingInputsMap(current => ({
            ...current,
            [marketingInputsKey]: { channelSpend, channelClassification }
          }));
        } catch (e) {
          setError({
            message: `Failed to get marketing inputs. ERROR: ${e.message}`,
            reportError: e
          });
        }
      });
    }
  }, [
    company,
    dates,
    companyInfo.initial_kpi,
    marketingInputsMap,
    marketingInputsKey,
    setError,
    dedupeFetch
  ]);

  const marketingInputs = useMemo(
    () => marketingInputsMap[marketingInputsKey]?.channelSpend,
    [marketingInputsMap, marketingInputsKey]
  );
  const channelClassification = useMemo(
    () => marketingInputsMap[marketingInputsKey]?.channelClassification,
    [marketingInputsMap, marketingInputsKey]
  );

  useEffect(() => {
    if (company && !filterData) {
      dedupeFetch(`getChannelFilterSourceOptions_${company}`, async () => {
        try {
          const res = await SingleChannelLambdaFetch('/getChannelFilterSourceOptions', {
            params: {
              company,
              channel: CROSS_CHANNEL,
              pageID: CROSS_CHANNEL
            }
          });

          // This endpoint returns filter options, platform options, AND source-of-truth options
          // in one payload — it supersedes getCrossChannelFilterOptions and getSourceOfTruthOptions.
          const [filterOptions, newPlatformOptions, newSourceOptions] =
            await parseBase64GzippedResponse<
              [CC.GetFilterOptionsResponse, PlatformOptions, { label: string; value: string }[]]
            >(res);

          const newCustomSegments = buildCustomSegmentDimensionMap(filterOptions);
          setFilterData(filterOptions);
          setCustomSegments(newCustomSegments);
          setPlatformOptions(newPlatformOptions);
          setSourceOptions(newSourceOptions || []);
        } catch (e) {
          setError({
            message: e.message,
            reportError: e
          });
        }
      });
    }
  }, [company, filterData, prefix, setError, setFilterData, setSourceOptions, dedupeFetch]);

  // Combine the default cross-channel dimensions with the company-specific custom segments
  const allDimensions = useMemo(
    () => ({ ...dimensionColumnMetaDataMap, ...customSegments }),
    [customSegments]
  );

  // Fetch page and table presets in parallel on mount and on company switch.
  useEffect(() => {
    if (!company) {
      return;
    }
    if (pagePresets && !R.map(R.prop('company'), pagePresets).includes(company)) {
      // Company switched via the nav bar — reset and reload both in parallel.
      getPagePresets();
      getTablePresets();
      setPagePresetName(undefined);
      resetPageSettings();
    } else {
      if (!pagePresets) {
        getPagePresets();
      }
      if (!tablePresets) {
        getTablePresets();
      }
    }
  }, [company, pagePresets, tablePresets, getPagePresets, getTablePresets, resetPageSettings]);

  // Derives the active tab from the matched route's pathname (`.../cross-channel/<tab>`). Kept as
  // a useState+useEffect rather than a useMemo because `selectedTab` is threaded through many
  // other memos/callbacks and the render switch below; a full remount already happens on every
  // tab switch (see `channelBasePath`/`setURL` above).
  useEffect(() => {
    if (R.isNil(selectedTab)) {
      if (location.pathname.includes(`/${TabKey.DELIVERY_AND_PERFORMANCE}`)) {
        setSelectedTab(TabKey.DELIVERY_AND_PERFORMANCE);
      } else if (location.pathname.includes(`/${TabKey.METRICS_TABLE}`)) {
        setSelectedTab(TabKey.METRICS_TABLE);
      } else if (location.pathname.includes(`/${TabKey.OVERVIEW}`)) {
        setSelectedTab(TabKey.OVERVIEW);
      } else if (location.pathname.includes(`/${TabKey.TOTAL_BUSINESS}`)) {
        setSelectedTab(TabKey.TOTAL_BUSINESS);
      } else {
        setSelectedTab(TabKey.OVERVIEW);
      }
    }
  }, [selectedTab, location.pathname]);

  useEffect(() => {
    if (
      selectedTab &&
      pagePresets &&
      pagePresets.length &&
      tablePresets &&
      tablePresets.length &&
      !fetchingPagePresets &&
      !fetchingTablePresets &&
      !pagePresetName // We want to only use this logic when we load up the page
    ) {
      const urlPagePreset = R.find(preset => preset.name === urlPagePresetName, pagePresets);
      const urlTablePreset = R.find(preset => preset.name === urlTablePresetName, tablePresets);
      // Page Preset Load Up Cases
      // 1. Page Preset from URL
      // 2. Default Page Preset defined
      // 3. Load first page preset that isn't temporary
      let defaultPagePreset = urlPagePreset;
      if (R.isNil(defaultPagePreset)) {
        for (const preset of pagePresets) {
          if (
            (preset && preset.globalOptions && preset.globalOptions.isDefault) ||
            (R.isNil(defaultPagePreset) && !preset.temporary)
          ) {
            defaultPagePreset = preset;
          }
        }
      }
      // Table Preset Load Up Cases
      // 1. Table Preset from URL
      // 2. Default Table Preset from the page preset selected
      // 3. Load first table preset that isn't temporary
      let defaultTablePreset = urlTablePreset;
      if (R.isNil(defaultTablePreset)) {
        for (const preset of tablePresets) {
          if (
            (preset && preset.id === defaultPagePreset?.globalOptions?.tableID) ||
            (R.isNil(defaultTablePreset) && !preset.temporary)
          ) {
            defaultTablePreset = preset;
          }
        }
      }
      if (defaultPagePreset && defaultTablePreset) {
        setURL(defaultPagePreset.name, defaultTablePreset.name);
      }
    }
  }, [
    fetchingPagePresets,
    fetchingTablePresets,
    pagePreset,
    pagePresetName,
    pagePresets,
    selectedTab,
    setURL,
    tablePresetName,
    tablePresets,
    urlPagePresetName,
    urlTablePresetName
  ]);

  const kpiNameMap = useMemo(() => {
    const map = {};
    for (const kpiName of crossChannelKPIs) {
      map[kpiName] = {
        cpxName: `CP${kpiName.slice(0).toUpperCase()}`,
        decimals: 0,
        id: kpiName,
        kpi: kpiName,
        name: kpiName,
        revenueHeader: null
      };
    }
    return map;
  }, [crossChannelKPIs]);

  const pagePresetGroups = useMemo(() => {
    const groupsMap = {};
    if (pagePresets) {
      for (const preset of pagePresets) {
        if (preset.presetGroup && preset.presetGroup !== STANDARD_GROUP_NAME) {
          groupsMap[preset.presetGroup] = true;
        }
      }
    }
    return R.keys(groupsMap).sort();
  }, [pagePresets]);

  // Page Preset Modals
  const editPagePresetModal = (showEditPagePresetModal || showNewPagePresetModal) &&
    kpiData &&
    pagePresetChanges && (
      <EditPagePresetModal
        mediatype='cross-channel'
        menuTabs={[
          'Dates',
          'KPI, etc.',
          'Cross-Channel Overview',
          'Delivery & Performance',
          'Metrics Table',
          'Filters',
          'Preset Group',
          'Total Business'
        ]}
        filterPresets={filterPresets}
        funnelPresets={kpiData.funnelPresets}
        pagePresetChanges={pagePresetChanges}
        globalOptionsPickers={{
          kpi: {
            options: R.map(
              kpiName => ({ label: kpiName, value: kpiName }),
              R.defaultTo([], crossChannelKPIs) as string[]
            )
          },
          source: sourceOptions
        }}
        groups={pagePresetGroups}
        sparkChartOptions={R.map(
          metric => ({ label: metric, value: metric }),
          [
            Metric.CLICKS_CONVERSION_RATE,
            Metric.IMPS_CONVERSION_RATE,
            Metric.CPM,
            Metric.CPX,
            Metric.CTR,
            Metric.CPC,
            Metric.IMPRESSIONS,
            Metric.REVENUE,
            Metric.ROAS,
            Metric.SPEND,
            Metric.VOLUME,
            Metric.CLICKS
          ]
        )}
        setPagePresetChanges={setPagePresetChanges}
        saveAndApplyOnClick={presetChanges => {
          confirmSave(
            () => {
              (async () => {
                const resolvedPresetChanges =
                  !R.isNil(presetChanges) &&
                  !R.isNil(presetChanges.filterInfo) &&
                  !R.isNil(presetChanges.filterInfo.filterState)
                    ? {
                        ...presetChanges,
                        filterInfo: { ...presetChanges.filterInfo, filterState }
                      }
                    : presetChanges;
                const presetResponse = await savePagePreset({
                  company,
                  id: R.defaultTo('new', resolvedPresetChanges.id),
                  mediatype: prefix,
                  name: resolvedPresetChanges.name,
                  group: resolvedPresetChanges.presetGroup,
                  preset: resolvedPresetChanges,
                  temporary: resolvedPresetChanges.temporary
                });
                if (
                  resolvedPresetChanges.temporary ||
                  (resolvedPresetChanges.id as number | string) === 'new' ||
                  resolvedPresetChanges.id <= 0 ||
                  (pagePreset && resolvedPresetChanges.id === pagePreset.id)
                ) {
                  resetPageSettings();
                  setURL(presetResponse.name, urlTablePresetName);
                }
                setShowEditPagePresetModal(false);
                setShowNewPagePresetModal(false);
              })();
            },
            { temporary: presetChanges.temporary }
          );
        }}
        tablePresets={R.filter(
          tablePreset => !tablePreset.temporary,
          R.defaultTo([], tablePresets)
        )}
        onHide={() => {
          setShowEditPagePresetModal(false);
          setShowNewPagePresetModal(false);
          if (pagePreset) {
            setPagePresetChanges(pagePreset);
          }
        }}
      />
    );

  const renamePagePresetModal = showRenamePagePresetModal && pagePresetChanges && (
    <RenameModal
      applyOnClick={newName => {
        confirmSave(() => {
          (async () => {
            await savePagePreset({
              company,
              id: pagePresetChanges.id,
              mediatype: prefix,
              name: newName,
              group: pagePresetChanges.presetGroup
            });
            setShowRenamePagePresetModal(false);
            setURL(newName, urlTablePresetName);
          })();
        });
      }}
      applyText='Save'
      cancelText='Discard'
      disabled={pagePresetChanges.globalOptions.admin_lock && isAdmin}
      onCancel={() => {
        setShowRenamePagePresetModal(false);
        if (pagePreset) {
          setPagePresetChanges(pagePreset);
        }
      }}
      onHide={() => {
        setShowRenamePagePresetModal(false);
        if (pagePreset) {
          setPagePresetChanges(pagePreset);
        }
      }}
      placeholder='Enter new preset name'
      subTitle='New Page Preset Name'
      title={`Rename ${pagePresetChanges && pagePresetChanges.name ? pagePresetChanges.name : ''}`}
    />
  );

  /*
    If a client has marketing inputs from TVAD, use that. Otherwise, use cross channel spend
    data to power the Marketing Inputs chart so that it's not blank.
  */
  const [totalBusinessMarketingInputs, totalBusinessChannelClassification] = useMemo(() => {
    if (R.isEmpty(marketingInputs) || showCCDataMarketingInputs) {
      const channelSpendByDate: Record<string, any> = {};
      const channels: Record<string, string> = {};
      const data = R.pathOr([], ['primaryMetrics'], metricsData);
      for (const item of data) {
        const { date, channel, metrics } = item;
        const { spend } = metrics;
        channels[channel] = 'Spend';

        if (!channelSpendByDate[date]) {
          channelSpendByDate[date] = {};
        }

        channelSpendByDate[date] = R.mergeDeepRight(channelSpendByDate[date], {
          [channel]: R.pathOr(0, [date, channel], channelSpendByDate) + spend
        });
      }

      const inputs = Object.entries(channelSpendByDate)
        .map(([date, channelSpend]) => ({
          date,
          ...channelSpend
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return [inputs, channels];
    }
    const channels: Record<string, string> = { ...channelClassification };
    for (const chan of R.keys(marketingInputs ? marketingInputs[0] || {} : {})) {
      if (chan !== 'date' && chan !== 'Interpolated' && !channels[chan as string]) {
        channels[chan as string] = 'Spend';
      }
    }
    return [marketingInputs, channels];
  }, [marketingInputs, channelClassification, metricsData, showCCDataMarketingInputs]);

  // Tab Render Item
  const renderItem: JSX.Element | undefined = useMemo(() => {
    if (
      !fetchingPagePresets &&
      pagePresetChanges &&
      !R.isNil(pagePresetChanges.id) &&
      customColumnMetaDataMap
    ) {
      const overview = (
        <Overview
          data={metricTotalsByDate}
          otherData={otherMetricTotalsByDate}
          defaultCompareMetrics={pagePresetChanges?.globalOptions?.compareMetrics}
          defaultCompareMetricsIncludeOtherDates={
            pagePresetChanges?.globalOptions?.compareMetricsIncludeOtherDates
          }
          defaultSparkCharts={pagePresetChanges?.globalOptions?.sparkCharts}
        />
      );
      switch (selectedTab) {
        case TabKey.OVERVIEW:
          return overview;
        case TabKey.DELIVERY_AND_PERFORMANCE:
          return (
            <DeliveryAndPerformance
              // getCrossChannelMetrics
              data={R.pathOr([], ['primaryMetrics'], metricsData)}
              otherData={R.pathOr([], ['otherMetrics'], metricsData)}
              // getCrossChannelBudgets
              budgetsData={budgetsData}
              defaultBudgetSnapshotBottom={pagePresetChanges?.globalOptions?.sbSnapshotBottom}
              defaultBudgetSnapshotTop={pagePresetChanges?.globalOptions?.sbSnapshotTop}
              defaultDeliveryIncludeOtherDates={
                pagePresetChanges?.globalOptions?.crossChannelDeliveryIncludeOtherDates
              }
              defaultDeliveryMetric={pagePresetChanges?.globalOptions?.crossChannelDeliveryMetric}
              defaultPerformanceBreakdownIncludeOtherDates={
                pagePresetChanges?.globalOptions?.crossChannelPerformanceIncludeOtherDates
              }
              defaultPerformanceBreakdownMetric={
                pagePresetChanges?.globalOptions?.crossChannelPerformanceMetric
              }
              defaultPerformanceSnapshotMetric={
                pagePresetChanges?.globalOptions?.crossChannelPerformanceSnapshotMetric
              }
              metricsDataLoading={metricsDataLoading}
            />
          );
        case TabKey.METRICS_TABLE:
          return (
            <Metrics
              columnMetaDataMap={{ ...columnMetaDataMap, ...customColumnMetaDataMap }}
              dates={R.defaultTo(startingDateRange, dates)}
              dimensionColumnMetaDataMap={allDimensions}
              getDimensionCell={(dimensionData: DimensionMap, dimensionHeader: DimensionColumn) =>
                getCrossChannelDimensionCell(dimensionData, dimensionHeader, fiscalCal)
              }
              kpi={R.defaultTo('', kpi)}
              kpiMetaData={kpiNameMap}
              metricsTableFetchFilterBar={metricsTableFetchFilterBar}
              prefix='cross_channel'
              refreshCustomMetrics={getCustomMetrics}
              setMetricsTableFetchFilterBar={setMetricsTableFetchFilterBar}
              groupedSegments={groupedSegments}
            />
          );
        case TabKey.TOTAL_BUSINESS:
          return (
            <TotalBusiness
              kpiInfo={kpiData}
              deliveryData={metricTotalsByDate}
              marketingInputs={totalBusinessMarketingInputs}
              channelClassification={totalBusinessChannelClassification}
              defaultFunnelDynamicsPreset={pagePresetChanges?.globalOptions?.funnelDynamicsName}
            />
          );
        default:
          return overview;
      }
    }
    return undefined;
  }, [
    allDimensions,
    budgetsData,
    customColumnMetaDataMap,
    dates,
    fetchingPagePresets,
    getCustomMetrics,
    kpi,
    kpiData,
    kpiNameMap,
    metricsData,
    metricsDataLoading,
    metricsTableFetchFilterBar,
    metricTotalsByDate,
    otherMetricTotalsByDate,
    pagePresetChanges,
    selectedTab,
    startingDateRange,
    columnMetaDataMap,
    totalBusinessMarketingInputs,
    totalBusinessChannelClassification,
    fiscalCal,
    groupedSegments
  ]);

  // Used for Excel exports
  const dataInfo = useMemo(() => {
    if (!filterData || !filterState) {
      return [];
    }

    const sources = selectedSources
      .map(source => (sourceOptions.find(option => option.value === source) || {}).label || source)
      .join(', ');

    const data = getDataInfo({
      kpi: kpi || '',
      sources,
      filterData,
      filterState
    });

    const exportData = dataInfoExcelExport(data);

    return exportData;
  }, [filterData, filterState, kpi, selectedSources, sourceOptions]);

  const crossChannelContextValue = useMemo(
    () => ({
      company,
      dates: R.defaultTo(startingDateRange, dates),
      otherDates: R.defaultTo(startingOtherDateRange, otherDates),
      selectedChannels,
      deliveryAndPerformanceChannels,
      channelColors,
      kpi: R.defaultTo('', kpi),
      crossChannelKPIs,
      platformAsSourceOfData,
      selectedSources,
      // URL
      setURL,
      // Page Preset
      pagePreset,
      // Filter Presets
      filterData,
      filterID,
      filterSource,
      filterState,
      setFilterData,
      // Table Preset
      fetchingTablePresets,
      getTablePresets,
      setFetchingTablePresets,
      tablePresetName,
      tablePresets,
      // Metrics Table
      metricsTableDataMap,
      setMetricsTableDataMap,
      metricsTableDataKey,
      metricsTableDataOtherKey,
      customSegments: R.keys(customSegments).reduce(
        (prev, curr) => ({ ...prev, [curr]: true }),
        {}
      ),
      dataInfo,
      fiscalCal,
      usedGroupedSegments
    }),
    [
      company,
      startingDateRange,
      dates,
      startingOtherDateRange,
      otherDates,
      selectedChannels,
      deliveryAndPerformanceChannels,
      channelColors,
      kpi,
      crossChannelKPIs,
      platformAsSourceOfData,
      selectedSources,
      setURL,
      pagePreset,
      filterData,
      filterID,
      filterSource,
      filterState,
      setFilterData,
      fetchingTablePresets,
      getTablePresets,
      setFetchingTablePresets,
      tablePresetName,
      tablePresets,
      metricsTableDataMap,
      setMetricsTableDataMap,
      metricsTableDataKey,
      metricsTableDataOtherKey,
      customSegments,
      dataInfo,
      fiscalCal,
      usedGroupedSegments
    ]
  );

  // The header's date pickers, source picker, and filter bar each populate from independent
  // fetches (dates, sourceOptions, filterData, pagePresets) that resolve at different times.
  // Gating them all on this single flag keeps them from popping in one at a time — they appear
  // together once everything they depend on is ready.
  const isPageHeaderReady = Boolean(dates && otherDates && filterData && pagePresets);
  // FetchFilterBar fetches its own saved-filter-preset list after it mounts (filterPresets starts
  // undefined and is populated via the setFilters callback we pass it) — it can't be gated on its
  // own readiness without a circular dependency. Instead it stays mounted (so that fetch can run)
  // but hidden via visibility, not display, so the sub-header's layout space is reserved from the
  // start and the whole bar still appears as one unit once its own data lands too.
  const isFilterBarReady = isPageHeaderReady && !R.isNil(filterPresets);

  return (
    <Page
      isLoading={
        !metricsData ||
        !kpiData ||
        !metricTotalsByDate ||
        !otherMetricTotalsByDate ||
        fetchingPagePresets
      }
      pageType={PAGE_NAMES.CROSS_CHANNEL}
      title={PAGE_NAMES.CROSS_CHANNEL}
      navs={NAVS}
      selectedNav={selectedTab}
      onNav={nav => {
        setSelectedTab(nav);
        if (pagePresetName) {
          setURL(pagePresetName, tablePresetName, nav);
        }
      }}
      actions={
        isPageHeaderReady && (
          <Toolbar className='cc-page-actions'>
            <PresetDropdown
              size={Size.MD}
              addPresetOnClick={() => {
                setShowNewPagePresetModal(true);
                // In the case that we are in the standard group already, clear this out
                setPagePresetChanges(
                  preset =>
                    ({
                      ...preset,
                      id: 'new',
                      name: 'New Page Preset!',
                      presetGroup: '',
                      dateInfo: {
                        ...preset.dateInfo,
                        dateType: 'relative',
                        comparison_dateType: 'relative',
                        relative_date_info: 'Last Month',
                        comparison_relative_date_info: 'Default'
                      },
                      globalOptions: {
                        ...R.defaultTo({}, preset.globalOptions),
                        admin_lock: false,
                        role: 10,
                        isDefault: false
                      }
                    }) as any
                );
              }}
              addPresetText='Add New Page Preset'
              applyOnClick={preset => {
                setURL(preset.name);
                resetPageSettings();
              }}
              cancelOnClick={() => {}}
              className={`pagePresetPicker ${fetchingPagePresets ? 'loading' : ''}`}
              deleteItemOnClick={presetID => deletePagePreset({ id: presetID, mediatype: prefix })}
              duplicateItemOnClick={preset =>
                confirmSave(() =>
                  savePagePreset({
                    company,
                    id: 'new',
                    mediatype: prefix,
                    preset: {
                      ...preset,
                      globalOptions: {
                        ...R.defaultTo({}, preset.globalOptions),
                        admin_lock: false
                      }
                    },
                    name: `${preset.name} (Copy)`,
                    group: preset.presetGroup
                  })
                )
              }
              editItemOnClick={preset => {
                const presetCopy = { ...preset };
                if (
                  preset.dateInfo &&
                  preset.dateInfo.dateType === 'relative' &&
                  !preset.dateInfo.relative_date_info
                ) {
                  presetCopy.dateInfo.dateType = 'exact';
                }
                if (preset.dateInfo && !preset.dateInfo.comparison_relative_date_info) {
                  presetCopy.dateInfo.comparison_dateType = 'exact';
                }
                setPagePresetChanges(presetCopy);
                setShowEditPagePresetModal(true);
              }}
              hasPresetGroups
              hasTicks
              label={
                pagePreset && !R.isNil(pagePreset.id)
                  ? `Page Preset: ${pagePreset.temporary ? 'Temp' : pagePreset.name}`
                  : 'Load Page Preset'
              }
              presets={R.filter(
                preset =>
                  !preset.temporary || (!R.isNil(pagePreset) && preset.id === pagePreset.id),
                R.defaultTo([], pagePresets)
              )}
              renameItemOnClick={preset => {
                setPagePresetChanges(preset);
                setShowRenamePagePresetModal(true);
              }}
              selectedPreset={pagePreset}
              setAsDefault={preset =>
                confirmSave(async () => {
                  const res = await savePagePreset({
                    company,
                    id: R.defaultTo('new', preset.id),
                    mediatype: prefix,
                    preset: {
                      ...preset,
                      globalOptions: { ...R.defaultTo({}, preset?.globalOptions), isDefault: true }
                    },
                    name: preset.name,
                    group: preset.presetGroup
                  });
                  if (res?.id) {
                    setError({
                      message: `${preset.name} (${res.id}) has been set as the default ${prefix} preset.`,
                      title: 'Success!',
                      intent: Intent.SUCCESS
                    });
                  }
                })
              }
              subTitles={['Preset Group', 'Preset']}
            />
            {!R.isEmpty(selectedSources) && selectedTab !== TabKey.TOTAL_BUSINESS && (
              <SourcePicker
                selectedSources={selectedSources}
                sourceOptions={sourceOptions}
                applyOnClick={setSelectedSources}
                platforms={platformOptions}
                size={Size.MD}
              />
            )}
            {selectedTab === TabKey.TOTAL_BUSINESS && (
              <SourcePicker
                selectedSources={totalBusinessSource ? [totalBusinessSource] : []}
                sourceOptions={sourceOptions}
                applyOnClick={sources => setTotalBusinessSource(sources[0])}
                platforms={platformOptions}
                singleSelect
                size={Size.MD}
              />
            )}
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.MD}
              dropdownMenuClassName='cc-kpi-picker'
              menuStrategy='fixed'
              label='KPI'
              value={R.defaultTo('', kpi)}
              options={crossChannelKPIs}
              onChange={option => {
                Mixpanel.track(MxE.PageKpiChange, {
                  widget: CrossChannelWidget.PAGE_KPI,
                  option_selected: option
                });
                setKpi(option);
              }}
            />
            <DatePicker
              range={dates}
              size={Size.MD}
              onChange={({ start, end }) => {
                onDatePickerChange({ start, end });
              }}
              comparison
              comparisonRange={otherDates}
              comparisonOnChange={({ start, end }) => {
                onOtherDatePickerChange({ start, end });
              }}
              datePresets
            />
          </Toolbar>
        )
      }
      subHeader={
        selectedTab !== TabKey.TOTAL_BUSINESS && (
          <div
            className='cc-page-sub-header'
            style={{ visibility: isFilterBarReady ? 'visible' : 'hidden' }}
          >
            {isPageHeaderReady && filterData && (
              <FetchFilterBar
                categories={filterData}
                groupedCategories={groupedSegments}
                company={company}
                filterID={filterID}
                filters={filterPresets}
                filterSource={filterSource}
                platform={prefix}
                refreshData={() => {
                  setMetricsDataMap(map => ({ ...map, [metricsDataKey]: undefined }));
                  setMetricsTableDataMap(map => ({ ...map, [metricsTableDataKey]: undefined }));
                }}
                setFilterID={setFilterID}
                setFilters={setFilterPresets}
                setFilterSource={
                  setFilterSource as Dispatch<
                    SetStateAction<
                      'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState'
                    >
                  >
                }
                setState={setFilterState}
                state={filterState}
              />
            )}
          </div>
        )
      }
    >
      {metricsData &&
      kpiData &&
      metricTotalsByDate &&
      otherMetricTotalsByDate &&
      !fetchingPagePresets ? (
        <CrossChannelContext.Provider value={crossChannelContextValue}>
          {editPagePresetModal}
          {renamePagePresetModal}
          <div className='cc-page'>
            <Suspense fallback={<FullPageSpinner />}>{renderItem}</Suspense>
          </div>
        </CrossChannelContext.Provider>
      ) : (
        <FullPageSpinner />
      )}
    </Page>
  );
};
